import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ManualSourceEditorModal = ({
  open,
  onClose,
  initialHtml = '',
  initialCss = '',
  initialVariantKey = 'manual',
  onSave,
  saving = false,
}) => {
  const [html, setHtml] = useState(initialHtml);
  const [css, setCss] = useState(initialCss);
  const [variantKey, setVariantKey] = useState(initialVariantKey);

  useEffect(() => {
    if (open) {
      setHtml(initialHtml);
      setCss(initialCss);
      setVariantKey(initialVariantKey);
    }
  }, [open, initialHtml, initialCss, initialVariantKey]);

  if (!open) return null;

  const handleSubmit = event => {
    event.preventDefault();
    if (typeof onSave === 'function') {
      onSave({
        html,
        css,
        variantKey: variantKey && variantKey.trim() ? variantKey.trim() : 'manual',
      });
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 px-4 py-6 overflow-y-auto">
      <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl max-h-[94vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Manual Source Editor</h2>
            <p className="text-sm text-slate-500">
              Paste custom HTML & CSS for this template. This bypasses the visual editor and is available to admins only.
            </p>
          </div>
          <button
            type="button"
            className="text-slate-400 transition hover:text-slate-600"
            onClick={onClose}
            aria-label="Close manual source editor"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="grid gap-6 md:grid-cols-2">
            <div className="md:col-span-1 md:row-span-2">
              <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="manual-source-html">
                HTML
              </label>
              <textarea
                id="manual-source-html"
                className="min-h-[220px] sm:h-96 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 font-mono text-sm text-slate-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={html}
                onChange={e => setHtml(e.target.value)}
                placeholder="<!DOCTYPE html>..."
                spellCheck={false}
              />
            </div>
            <div className="md:col-span-1">
              <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="manual-source-css">
                CSS (optional)
              </label>
              <textarea
                id="manual-source-css"
                className="min-h-[180px] sm:h-60 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 font-mono text-sm text-slate-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={css}
                onChange={e => setCss(e.target.value)}
                placeholder="body { font-family: ... }"
                spellCheck={false}
              />
            </div>
            <div className="md:col-span-1 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="manual-source-variant">
                  Variant key
                </label>
                <input
                  id="manual-source-variant"
                  type="text"
                  value={variantKey}
                  onChange={e => setVariantKey(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="manual"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Use different keys to store multiple layouts (e.g. desktop, regmail). The active variant will switch to this key.
                </p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <p className="font-semibold">Heads up</p>
                <p>
                  Saving will overwrite the visual editor output for this variant. It cannot be undone without a snapshot.
                </p>
              </div>
            </div>
            </div>
          </div>
          <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 w-full sm:w-auto"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/50 w-full sm:w-auto"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save source'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ManualSourceEditorModal;
